/* eslint-disable react-hooks/rules-of-hooks */
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import { debounce } from 'lodash';
import useAssetEventsLength from 'sdk/hooks/assets/useAssetEventsLength';

import supabase from '../../api';
import { getCurrencySybol } from '../../utils/currency';
import { formatAssetStat } from '../../utils/formatValues';
import useAsset from '../useAsset';
import useAssetEvents from '../useAssetEvents';

export type SelectedPrice = {
  price: number;
  time: string;
};

const getPeriods = (isNft: boolean, isCrypto: boolean, onlyDay?: boolean) => {
  if (onlyDay) return isCrypto || isNft ? ['24H'] : ['1D'];

  return isNft
    ? ['24H', '7D', '30D', '90D', '5Y', '']
    : [isCrypto ? '24H' : '1D', '1W', '1M', '6M', '1Y', '5Y'];
};

export const useAssetChartData = (assetId: string, onlyDay?: boolean) => {
  const [isPressIn, setPressIn] = useState(false);
  const [isFall, setIsFall] = useState(false);
  const [lastPrice, setLastPrice] = useState(0);

  const [period, setPeriod] = useState('');
  const [selectedPrice, setSelectedPrice] = useState<SelectedPrice | null>(
    null,
  );
  const [isPreMarket, setIsPreMarket] = useState(false);
  const [isAfterHours, setIsAfterHours] = useState(false);

  const { data: assetData } = useAsset(assetId);
  const currency = getCurrencySybol(assetData?.currency_name);

  const handleUpdatePrice = (data: any) => {
    if (assetData?.quote?.is_market_open) {
      setLastPrice(data?.new?.latest_price?.price || 0);
    }
  };

  useEffect(() => {
    let channel: any;
    if (assetId) {
      channel = supabase
        .channel('supabase_realtime')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'assets',
            filter: `id=eq.${assetId}`,
          },
          handleUpdatePrice,
        )
        .subscribe();
    }
    return () => {
      if (channel) channel.unsubscribe();
    };
  }, [assetId, handleUpdatePrice]);

  const category = assetData?.category?.name || '';

  const isCurrency = category === 'Economy' || category === 'Crypto';
  const isCrypto = category === 'Crypto';
  const isNft = category === 'Economy'; // 'Business and Money';
  const isETF = category === 'ETFs';
  const isStock = category === 'Stocks';

  const periods = useMemo(
    () => getPeriods(isNft, isCrypto, onlyDay),
    [isCrypto, isNft, onlyDay],
  );
  const assetEventDataForDay = periods && useAssetEvents(assetId, periods[0], category === 'Economy');

  const isYear = useMemo(() => ['1Y', '5Y'].includes(period), [period]);
  const showDays = useMemo(() => !['24H', '1D'].includes(period), [period]);
  const showTime = useMemo(
    () => ['24H', '1D', '7D', '14D', '1W', '1M'].includes(period),
    [period],
  );
  const assetEventDataAll = periods.map((itemPeriod) => useAssetEvents(
    assetId,
    itemPeriod,
    category === 'Economy',
  ));

  const { data: assetEventsYesterday } = useAssetEventsLength(
    assetId,
    period,
    category === 'Economy',
  );

  const assetEventData = useMemo(() => {
    if (period === periods[0]) {
      return assetEventDataForDay ?? [];
    }
    return assetEventDataAll[periods.indexOf(period)] ?? [];
  }, [assetEventDataForDay, assetEventDataAll, period]);

  const isOpenMarket = assetData?.quote?.is_market_open;

  let oldPrice = assetEventData.data?.[0]?.close || 0;
  if (['1D', '24H'].includes(period) && assetData?.prev_day_price?.price) {
    oldPrice = Number(assetData.prev_day_price.price);
  }
  if (!isOpenMarket && typeof assetData?.quote?.close === 'number') {
    oldPrice = Number(assetData.quote?.close);
  }

  const deltaDefault = lastPrice - oldPrice;

  let delta = isCurrency && selectedPrice?.price
    ? selectedPrice.price - oldPrice
    : deltaDefault;
  if (isPressIn) {
    delta = (selectedPrice?.price || 0) - oldPrice;
  }

  let deltaPercent: number;
  if (isPressIn) {
    deltaPercent = lastPrice ? (delta / lastPrice) * 100 : 0;
  } else {
    deltaPercent = oldPrice ? (deltaDefault / oldPrice) * 100 : 0;
  }

  // T16:00
  const afterHoursPrice = assetEventData.data?.length
    ? assetEventData.data.find((el) => el.datetime.includes('T16:00:00+00:00'))
      ?.close
    : 0;

  const afterHoursDelta = Math.round(100 * ((selectedPrice?.price || 0) - afterHoursPrice)) / 100;

  const afterHoursDeltaPercent = Math.round(
    10000 * (lastPrice ? afterHoursDelta / lastPrice : 0),
  ) / 100;

  useEffect(() => {
    setIsFall(deltaDefault < 0);
  }, [deltaDefault]);

  useEffect(() => {
    let price = assetData?.latest_price?.price || 0;

    if (!assetData?.quote?.is_market_open) {
      price = assetData?.quote.close || 0;
    }
    setLastPrice(price);
    setSelectedPrice({ price, time: '' });
  }, [assetData?.latest_price?.price]);

  useEffect(() => {
    if (lastPrice && !selectedPrice) {
      setSelectedPrice({ price: lastPrice, time: '' });
    }
  }, [lastPrice, selectedPrice]);

  const selectPriceHandler = useCallback(
    (price: SelectedPrice | null) => {
      if (price?.price !== selectedPrice?.price) {
        setSelectedPrice(price);
      }
    },
    [selectedPrice?.price, setSelectedPrice],
  );

  useEffect(() => {
    setPeriod(category === 'Economy' || isCrypto ? '24H' : '1D');
  }, [isCurrency]);

  const debouncedSelectPriceHandler = useMemo(
    () => debounce(selectPriceHandler, 17),
    [selectPriceHandler],
  );

  const color = useMemo(() => {
    if (!assetEventData.data?.length) return 'rgba(255, 255, 255, 0.3))';

    return isFall ? 'rgba(255, 77, 48, 0.5)' : 'rgba(32,102,59,255)';
  }, [assetEventData, isFall]);

  const changeIsPreMarket = useCallback(
    (value: boolean) => {
      if (showDays) return;

      setIsPreMarket(value);
    },
    [setIsPreMarket, setIsAfterHours, showDays],
  );

  const changeIsAfterHours = useCallback(
    (value: boolean) => {
      if (showDays) return;

      setIsAfterHours(value);
    },
    [setIsAfterHours, setIsPreMarket, showDays],
  );

  const getTopText = useCallback(() => {
    const sign = delta >= 0 ? '+' : '-';
    const currencySymbol = getCurrencySybol(assetData?.currency_name);

    const deltaValue = isOpenMarket || isPressIn || period !== '1D'
      ? formatAssetStat(Math.abs(delta))
      : formatAssetStat(Math.abs(assetData?.quote?.change));

    const deltaPercentValue = isOpenMarket || isPressIn || period !== '1D'
      ? formatAssetStat(Math.abs(deltaPercent), true, true)
      : formatAssetStat(
        Math.abs(assetData?.quote?.percent_change),
        true,
        true,
      );

    let additionalText = '';
    if (selectedPrice?.time && isPreMarket && period === '1D') {
      additionalText += ' Pre-market';
    } else if (period === '1D' && !isAfterHours) {
      additionalText += ' Today';
    }

    return `${sign} ${currencySymbol} ${deltaValue} (${deltaPercentValue}%)${additionalText}`;
  }, [
    isPressIn,
    isAfterHours,
    isOpenMarket,
    isPreMarket,
    period,
    assetData,
    selectedPrice,
    delta,
    deltaPercent,
  ]);

  const getBottomText = useCallback(() => {
    if (showDays || Number.isNaN(afterHoursDelta) || !isAfterHours) return '';

    let sign = '';
    if (isAfterHours) {
      sign = assetData?.quote?.extended_change >= 0 ? '+' : '-';
    }
    // eslint-disable-next-line
    return `${sign} ${currency} ${isOpenMarket || isPressIn   // eslint-disable-next-line
      ? formatAssetStat(Math.abs(afterHoursDelta))   // eslint-disable-next-line
      : formatAssetStat(Math.abs(assetData?.quote?.extended_change), true)   // eslint-disable-next-line
      } (${isOpenMarket || isPressIn   // eslint-disable-next-line
        ? formatAssetStat(Math.abs(afterHoursDeltaPercent), true, true)   // eslint-disable-next-line
        : formatAssetStat(   // eslint-disable-next-line
          Math.abs(assetData?.quote?.extended_percent_change),   // eslint-disable-next-line
          true,   // eslint-disable-next-line
          true,   // eslint-disable-next-line
        )   // eslint-disable-next-line
      }%) After-hours`;
  }, [
    isAfterHours,
    isOpenMarket,
    isPressIn,
    assetData,
    afterHoursDelta,
    showDays,
  ]);

  return {
    isPressIn,
    setPressIn,
    isFall,
    setIsFall,
    lastPrice,
    setLastPrice,
    period,
    setPeriod,
    selectedPrice,
    selectPriceHandler: debouncedSelectPriceHandler,
    isPreMarket,
    setIsPreMarket: changeIsPreMarket,
    isAfterHours,
    setIsAfterHours: changeIsAfterHours,
    color,
    category,
    assetData,
    periods,
    assetEventsTotalLength: assetEventsYesterday?.length,
    assetEventData,
    oldPrice,
    deltaDefault,
    delta,
    deltaPercent,
    afterHoursPrice,
    afterHoursDeltaPercent,
    afterHoursDelta,
    isNft,
    isETF,
    isCrypto,
    isStock,
    hasEventsData: !!assetEventData.data?.length,
    isYear,
    isCurrency,
    showDays,
    showTime,
    isOpenMarket,
    getTopText,
    getBottomText,
    assetEventDataAll,
    assetEventDataForDay,
  };
};

export default useAssetChartData;
